/**
 * When a schedule template runs in one organization (Feature 77 D11, 4.1
 * to 4.3): on the template's default cron or the organization's own, set
 * here at any time and cleared back to the default; and what the agent
 * says of it here, the next slot, the last fire, and how many fires, with
 * who its steps run as and where they are delivered. The slots are the
 * agent's; its own is said with the schedule builder (Feature 81), starting
 * from the template's, and previewed with the console's own slots.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { setOrganizationLinkage } from '@optum/api-client/endpoints/organization-linkages';
import type { OrganizationLinkage } from '@optum/api-client/types';

import { RecordFacts, RecordSection } from '../../components/shell/RecordParts.js';
import { isValidCron, slotText, viewerTimeZone } from '../../lib/cron.js';
import { qk } from '../../lib/query-keys.js';
import { cronReading } from '../../lib/schedule.js';
import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { Stack, Typography } from '../../ui/finixt.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { useToast } from '../../ui/useToast.js';
import { ScheduleBuilder } from '../linkages/ScheduleBuilder.js';
import { refusalText } from '../linkages/TenantLinkages.js';

type Choice = 'default' | 'own';

/** An instant the agent stored, in the viewer's timezone, or a dash. */
function when(at: string | null | undefined, timeZone: string): string {
  return at ? slotText(new Date(at), timeZone) : '—';
}

export function ScheduleHere({ orgId, row }: { orgId: string; row: OrganizationLinkage }) {
  const qc = useQueryClient();
  const toast = useToast();
  const timeZone = viewerTimeZone();
  const stored = row.cron_expression ?? null;
  const [choice, setChoice] = useState<Choice>(stored ? 'own' : 'default');
  const defaultCron = row.start?.cron ?? '';
  const [cron, setCron] = useState(stored ?? defaultCron);
  const wanted = choice === 'own' ? cron.trim() : null;
  const save = useMutation({
    mutationFn: () => setOrganizationLinkage(orgId, row.key, row.enabled, wanted),
    onSuccess: () => {
      toast.success(wanted ? 'Its own cron is set' : 'Back on the template’s default');
      void qc.invalidateQueries({ queryKey: qk.organizationLinkages(orgId) });
    },
    onError: (e) => toast.error('Not saved', refusalText(e)),
  });
  const changed = wanted !== stored;
  const ready = wanted === null || isValidCron(wanted);
  const defaultReading = cronReading(defaultCron, timeZone);

  return (
    <RecordSection label="When it runs here">
      <Stack gap={9}>
        <SegmentedControl
          items={[
            { value: 'default', label: 'The template’s default' },
            { value: 'own', label: 'Its own' },
          ]}
          value={choice}
          onChange={(next) => setChoice(next as Choice)}
        />
        {choice === 'default' ? (
          <Typography variant="meta">
            {defaultReading ? `${defaultReading} · ` : ''}
            <span className="mono">{defaultCron} UTC</span>
          </Typography>
        ) : (
          <Field id={`own-when-${row.key}`} label="Its own schedule">
            <ScheduleBuilder
              id={`own-when-${row.key}`}
              cron={cron}
              onChange={(next) => setCron(next ?? '')}
            />
          </Field>
        )}
        {changed && (
          <Button size="sm" disabled={!ready || save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        )}
        <RecordFacts
          items={[
            { label: 'Next', value: row.enabled ? when(row.next_fire_at, timeZone) : 'off here' },
            { label: 'Last fire', value: when(row.last_fired_at, timeZone) },
            { label: 'Fires', value: <span className="mono">{row.fire_count ?? 0}</span> },
            { label: 'Runs as', value: 'The automation user, on the automation agent' },
            {
              label: 'Delivered to',
              value: row.needs_webhook ? 'no endpoint yet' : 'the webhook endpoint',
            },
          ]}
        />
      </Stack>
    </RecordSection>
  );
}
